import React, { useState, useEffect, useCallback } from "react"
import {
    Row, Col, Card, CardBody, Button, Spinner, Alert, Badge,
    Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import axiosClient from "../../helpers/axiosClient"

/* ═══════════════════════════════════════════════════════════════
   GestionMenus — CRUD de menús del sistema (Super Admin)
   ═══════════════════════════════════════════════════════════════ */
const GestionMenus = ({ setBreadcrumbItems, user }) => {
    document.title = "Gestión de Menús | LS-DA - Softlogy"

    const [menus, setMenus] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [successMsg, setSuccessMsg] = useState("")

    // Modal crear/editar
    const [modalOpen, setModalOpen] = useState(false)
    const [editando, setEditando] = useState(null)
    const [codigo, setCodigo] = useState("")
    const [nombre, setNombre] = useState("")
    const [icono, setIcono] = useState("")
    const [ruta, setRuta] = useState("")
    const [orden, setOrden] = useState(1)
    const [parentId, setParentId] = useState("")
    const [saving, setSaving] = useState(false)
    const [modalError, setModalError] = useState("")

    // Breadcrumb
    useEffect(() => {
        const greeting = user ? `Bienvenido, ${user.nombre} ${user.apellido}` : "Dashboard"
        setBreadcrumbItems(greeting, [
            { title: "LS-DA", link: "#" },
            { title: "Seguridad", link: "#" },
            { title: "Gestión de Menús", link: "#" },
        ])
    }, [setBreadcrumbItems, user])

    // Fetch
    const fetchMenus = useCallback(async () => {
        setLoading(true)
        setError("")
        try {
            const res = await axiosClient.get("menus")
            const data = res.data.data || res.data
            setMenus(Array.isArray(data) ? data : [])
        } catch (err) {
            setError(err.response?.data?.message || "Error al obtener menús")
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => { fetchMenus() }, [fetchMenus])

    // Padres para select
    const menusPadre = menus.filter(m => !m.parentId)

    // Open crear
    const handleOpenCrear = (padreId = "") => {
        setEditando(null)
        setCodigo("")
        setNombre("")
        setIcono("")
        setRuta("")
        setOrden(1)
        setParentId(padreId)
        setModalError("")
        setModalOpen(true)
    }

    // Open editar
    const handleOpenEditar = (menu) => {
        setEditando(menu)
        setCodigo(menu.codigo || "")
        setNombre(menu.nombre || "")
        setIcono(menu.icono || "")
        setRuta(menu.ruta || "")
        setOrden(menu.orden || 1)
        setParentId(menu.parentId || "")
        setModalError("")
        setModalOpen(true)
    }

    // Save
    const handleSave = async () => {
        if (!codigo.trim() || !nombre.trim()) {
            setModalError("Código y nombre son obligatorios")
            return
        }
        setSaving(true)
        setModalError("")
        try {
            const payload = {
                codigo: codigo.trim(),
                nombre: nombre.trim(),
                icono: icono.trim() || null,
                ruta: ruta.trim() || null,
                orden: parseInt(orden) || 1,
            }
            if (parentId) payload.parentId = parentId

            if (editando) {
                await axiosClient.put(`menus/${editando.id}`, payload)
                setSuccessMsg(`Menú "${nombre}" actualizado`)
            } else {
                await axiosClient.post("menus", payload)
                setSuccessMsg(`Menú "${nombre}" creado`)
            }
            setTimeout(() => setSuccessMsg(""), 3000)
            setModalOpen(false)
            fetchMenus()
        } catch (err) {
            const msg = err.response?.data?.message || "Error al guardar menú"
            setModalError(typeof msg === "string" ? msg : JSON.stringify(msg))
        } finally {
            setSaving(false)
        }
    }

    // Total count flat
    const totalMenus = menus.reduce((acc, m) => acc + 1 + (m.hijos?.length || 0), 0)

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
                        <CardBody style={{ padding: "2rem" }}>
                            {/* Header */}
                            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                                <div>
                                    <h4 className="mb-1" style={{ fontWeight: 700 }}>
                                        <i className="mdi mdi-sitemap-outline me-2" style={{ color: "#10b981" }} />
                                        Gestión de Menús
                                    </h4>
                                    <small className="text-muted">Administra las pantallas y menús del sistema</small>
                                </div>
                                <Button color="success" onClick={() => handleOpenCrear()} style={{ borderRadius: 8 }}>
                                    <i className="mdi mdi-plus me-1" />Nuevo Menú
                                </Button>
                            </div>

                            {/* Alerts */}
                            {successMsg && (
                                <Alert color="success" className="mb-3">
                                    <i className="mdi mdi-check-circle me-1" />{successMsg}
                                </Alert>
                            )}
                            {error && (
                                <Alert color="danger" className="mb-3">
                                    <i className="mdi mdi-alert-circle me-1" />{error}
                                    <Button color="link" size="sm" className="ms-2 p-0" onClick={fetchMenus}>Reintentar</Button>
                                </Alert>
                            )}

                            {/* Loading */}
                            {loading && (
                                <div className="text-center py-5">
                                    <Spinner color="primary" />
                                    <p className="mt-2 text-muted">Cargando menús...</p>
                                </div>
                            )}

                            {/* Menu Tree */}
                            {!loading && menus.length > 0 && (
                                <div>
                                    {menus.map(menu => (
                                        <div key={menu.id} style={{
                                            marginBottom: 16, border: "1px solid #e2e8f0",
                                            borderRadius: 12, overflow: "hidden",
                                        }}>
                                            {/* Padre */}
                                            <div style={{
                                                padding: "12px 16px", backgroundColor: "#f8fafc",
                                                display: "flex", alignItems: "center", gap: 12,
                                                borderBottom: menu.hijos?.length ? "1px solid #e2e8f0" : "none",
                                            }}>
                                                <div style={{
                                                    width: 36, height: 36, borderRadius: 8,
                                                    backgroundColor: "#10b981", display: "flex",
                                                    alignItems: "center", justifyContent: "center",
                                                }}>
                                                    <i className={menu.icono || "mdi mdi-menu"} style={{ color: "#fff", fontSize: 18 }} />
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 600, fontSize: "0.9rem", color: "#1e293b" }}>{menu.nombre}</div>
                                                    <div className="d-flex gap-2 align-items-center mt-1">
                                                        <code style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{menu.codigo}</code>
                                                        {menu.ruta && <code style={{ fontSize: "0.72rem", color: "#64748b" }}>{menu.ruta}</code>}
                                                        <Badge color="light" style={{ color: "#94a3b8", fontSize: "0.68rem" }}>Orden: {menu.orden}</Badge>
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-1">
                                                    <Button color="outline-success" size="sm" title="Agregar Sub-menú"
                                                        onClick={() => handleOpenCrear(menu.id)}
                                                        style={{ borderRadius: 6 }}>
                                                        <i className="mdi mdi-plus" />
                                                    </Button>
                                                    <Button color="outline-warning" size="sm" title="Editar"
                                                        onClick={() => handleOpenEditar(menu)}
                                                        style={{ borderRadius: 6 }}>
                                                        <i className="mdi mdi-pencil-outline" />
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Hijos */}
                                            {menu.hijos?.length > 0 && (
                                                <div style={{ padding: "0" }}>
                                                    {menu.hijos.map((hijo, idx) => (
                                                        <div key={hijo.id} style={{
                                                            padding: "10px 16px 10px 60px",
                                                            display: "flex", alignItems: "center", gap: 10,
                                                            borderBottom: idx < menu.hijos.length - 1 ? "1px solid #f1f5f9" : "none",
                                                            backgroundColor: "#fff",
                                                        }}>
                                                            <i className="mdi mdi-subdirectory-arrow-right" style={{ color: "#cbd5e1", fontSize: 16 }} />
                                                            <div style={{ flex: 1 }}>
                                                                <span style={{ fontWeight: 500, fontSize: "0.85rem", color: "#475569" }}>{hijo.nombre}</span>
                                                                <div className="d-flex gap-2 align-items-center mt-1">
                                                                    <code style={{ fontSize: "0.7rem", color: "#94a3b8" }}>{hijo.codigo}</code>
                                                                    {hijo.ruta && <code style={{ fontSize: "0.7rem", color: "#64748b" }}>{hijo.ruta}</code>}
                                                                    <Badge color="light" style={{ color: "#94a3b8", fontSize: "0.65rem" }}>Orden: {hijo.orden}</Badge>
                                                                </div>
                                                            </div>
                                                            <Button color="outline-warning" size="sm" title="Editar"
                                                                onClick={() => handleOpenEditar(hijo)}
                                                                style={{ borderRadius: 6 }}>
                                                                <i className="mdi mdi-pencil-outline" />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Empty */}
                            {!loading && menus.length === 0 && !error && (
                                <div className="text-center py-5">
                                    <i className="mdi mdi-sitemap-outline" style={{ fontSize: 48, color: "#cbd5e1" }} />
                                    <p className="text-muted mt-2 mb-0">No hay menús configurados</p>
                                </div>
                            )}

                            {/* Count */}
                            {!loading && menus.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <small className="text-muted">
                                        {totalMenus} menú{totalMenus !== 1 ? "s" : ""} en total ({menus.length} principales)
                                    </small>
                                    <Button color="outline-secondary" size="sm" onClick={fetchMenus} style={{ borderRadius: 6 }}>
                                        <i className="mdi mdi-refresh me-1" />Actualizar
                                    </Button>
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ══════ Modal: Crear/Editar Menú ══════ */}
            <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
                <ModalHeader toggle={() => setModalOpen(false)}>
                    <i className={`mdi ${editando ? "mdi-pencil" : "mdi-plus-circle"} me-2`} style={{ color: "#10b981" }} />
                    {editando ? `Editar: ${editando.nombre}` : parentId ? "Nuevo Sub-menú" : "Nuevo Menú Principal"}
                </ModalHeader>
                <ModalBody>
                    {modalError && (
                        <Alert color="danger" className="mb-3">
                            <i className="mdi mdi-alert-circle me-1" />{modalError}
                        </Alert>
                    )}

                    <Row className="mb-3">
                        <Col md={6}>
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>
                                Código <span style={{ color: "#dc3545" }}>*</span>
                            </label>
                            <input type="text" className="form-control" value={codigo}
                                onChange={e => setCodigo(e.target.value)}
                                placeholder="Ej: nomina"
                                disabled={!!editando}
                                style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }} />
                        </Col>
                        <Col md={6}>
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>
                                Nombre <span style={{ color: "#dc3545" }}>*</span>
                            </label>
                            <input type="text" className="form-control" value={nombre}
                                onChange={e => setNombre(e.target.value)}
                                placeholder="Ej: Nómina"
                                style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }} />
                        </Col>
                    </Row>

                    <Row className="mb-3">
                        <Col md={6}>
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>
                                Ícono <small className="text-muted">(clase CSS)</small>
                            </label>
                            <input type="text" className="form-control" value={icono}
                                onChange={e => setIcono(e.target.value)}
                                placeholder="Ej: mdi mdi-receipt"
                                style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }} />
                            {icono && (
                                <div className="mt-1">
                                    <small className="text-muted me-2">Preview:</small>
                                    <i className={icono} style={{ fontSize: 20, color: "#556ee6" }} />
                                </div>
                            )}
                        </Col>
                        <Col md={6}>
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>Orden</label>
                            <input type="number" className="form-control" value={orden} min={1}
                                onChange={e => setOrden(e.target.value)}
                                style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }} />
                        </Col>
                    </Row>

                    <div className="mb-3">
                        <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>
                            Ruta <small className="text-muted">(dejar vacío si es menú padre sin página)</small>
                        </label>
                        <input type="text" className="form-control" value={ruta}
                            onChange={e => setRuta(e.target.value)}
                            placeholder="Ej: /portal/nomina/empleados"
                            style={{ borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }} />
                    </div>

                    {!editando && (
                        <div className="mb-3">
                            <label className="form-label" style={{ fontWeight: 600, fontSize: "0.85rem", color: "#334155" }}>
                                Menú Padre <small className="text-muted">(vacío = menú principal)</small>
                            </label>
                            <select value={parentId} onChange={e => setParentId(e.target.value)}
                                style={{ width: "100%", padding: "8px 12px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: "0.9rem", outline: "none", backgroundColor: "#f8fafc" }}>
                                <option value="">Sin padre (menú principal)</option>
                                {menusPadre.map(mp => (
                                    <option key={mp.id} value={mp.id}>{mp.nombre}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" size="sm" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</Button>
                    <Button color="success" size="sm" onClick={handleSave} disabled={saving} style={{ minWidth: 120, borderRadius: 8 }}>
                        {saving ? <><Spinner size="sm" className="me-1" />Guardando...</> : <><i className="mdi mdi-content-save me-1" />{editando ? "Actualizar" : "Crear"}</>}
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(GestionMenus)
